import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  Typography,
  alpha,
  type SxProps,
  type Theme,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import type { ReportChatShellColors } from "../../../lib/ui/reportChatShell";

const CARD_HEIGHT = 34;
const GAP = 6;
/** How many cards fit in the expanded list before it scrolls. */
const MAX_VISIBLE = 4;
/** Cards peeking out under the top card while stacked. */
const STACK_DEPTH = 3;
const STACK_OFFSET = 6;

interface Props {
  steps: string[];
  /** The step running now, shown as the thinking line above the cards. */
  current: string;
  label: string;
  c: ReportChatShellColors;
  textSx?: SxProps<Theme>;
}

/**
 * Progress steps as a deck of cards: stacked (latest on top, a fixed height) until clicked,
 * then laid out top-to-bottom in a list that shows at most {@link MAX_VISIBLE} cards and scrolls.
 */
export default function ChatProgressSteps({
  steps,
  current,
  label,
  c,
  textSx,
}: Props) {
  const [showCards, setShowCards] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const n = steps.length;
  const pitch = CARD_HEIGHT + GAP;

  // Keep the newest step in view while expanded.
  useEffect(() => {
    const el = scrollRef.current;
    if (!expanded || !el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [expanded, n]);

  // A click anywhere outside the opened list folds it back into the stack.
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        scrollRef.current?.contains(event.target)
      )
        return;
      setExpanded(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [expanded]);

  const stackHeight =
    CARD_HEIGHT + (Math.min(n, STACK_DEPTH) - 1) * STACK_OFFSET;
  const listHeight = Math.min(n, MAX_VISIBLE) * pitch - GAP;

  return (
    // Fixed width: the cards are absolutely positioned, so a percentage width would collapse
    // inside the shrink-to-fit thinking row.
    <Box sx={{ width: 440, maxWidth: "100%" }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          variant="body2"
          sx={{ ...(textSx as object), minWidth: 0 }}
          noWrap
        >
          {current}
        </Typography>
        <IconButton
          size="small"
          aria-label={`${label} (${n})`}
          aria-expanded={showCards}
          title={`${label} · ${n}`}
          onClick={() => {
            setShowCards((prev) => !prev);
            setExpanded(false);
          }}
          sx={{ p: 0.25, color: c.muted, flexShrink: 0 }}
        >
          <ExpandMoreRoundedIcon
            fontSize="small"
            sx={{
              transition: "transform 220ms",
              transform: showCards ? "rotate(180deg)" : "none",
            }}
          />
        </IconButton>
      </Box>
      <Collapse in={showCards} timeout={220} unmountOnExit>
        <Box
          ref={scrollRef}
          sx={{
            position: "relative",
            height: expanded ? listHeight : stackHeight,
            overflowY: expanded ? "auto" : "hidden",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            transition: "height 320ms cubic-bezier(.2,.8,.2,1)",
            scrollbarWidth: "thin",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: expanded ? n * pitch - GAP : stackHeight,
            }}
          >
            {steps.map((text, i) => {
              const depth = n - 1 - i;
              const latest = depth === 0;
              const peeking = depth < STACK_DEPTH;
              const transform = expanded
                ? `translateY(${i * pitch}px) scale(1)`
                : `translateY(${Math.min(depth, STACK_DEPTH - 1) * STACK_OFFSET}px) scale(${
                    1 - Math.min(depth, STACK_DEPTH) * 0.04
                  })`;
              return (
                <ButtonBase
                  key={`${i}-${text}`}
                  disableRipple={expanded}
                  onClick={() => setExpanded(true)}
                  aria-expanded={expanded}
                  aria-label={`${label} (${n})`}
                  tabIndex={latest || expanded ? 0 : -1}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: CARD_HEIGHT,
                    px: 1.25,
                    gap: 1,
                    justifyContent: "flex-start",
                    borderRadius: "10px",
                    border: `1px solid ${alpha(c.border, latest ? 1 : 0.8)}`,
                    bgcolor: c.surface,
                    boxShadow:
                      expanded || latest
                        ? `0 1px 4px ${alpha("#000", 0.12)}`
                        : "none",
                    transformOrigin: "50% 100%",
                    transform,
                    opacity: expanded || peeking ? 1 : 0,
                    zIndex: expanded ? 1 : n - depth,
                    pointerEvents: expanded || latest ? "auto" : "none",
                    transition: `transform 320ms cubic-bezier(.2,.8,.2,1) ${
                      expanded ? Math.min(i, 8) * 18 : 0
                    }ms, opacity 200ms, box-shadow 200ms`,
                    "&:hover": { borderColor: c.muted },
                    "@media (prefers-reduced-motion: reduce)": {
                      transition: "none",
                    },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      color: c.muted,
                      minWidth: 18,
                      textAlign: "right",
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      color: latest ? c.assistantText : c.muted,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {text}
                  </Typography>
                  {latest && !expanded && n > 1 && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: c.muted,
                        px: 0.75,
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        flexShrink: 0,
                      }}
                    >
                      {label} · {n}
                    </Typography>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
